import { debounce } from "lodash";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import apiFactory from "../../api";
import emojiSprite from "../../assets/sheet_facebook_32.png";
import { useMenuContext } from "../../context/MenuContext.js";
import { emojiMap } from "../moodPack/emoji/emojiMap.js";
import "./ContentEditableRichInput.scss";

/**
 * ContentEditableMentions
 * Props:
 * - value: markup string like "hello @ world"
 * - onChange: fn({ target: { value }, mentions })
 * - handleDataMentions(search, callback)
 * - inputRef (optional)
 * - renderSuggestion(item)
 * - allowSpaceInQuery, appendSpaceOnAdd, onPaste, onKeyDown, onClick
 */
export const ContentEditableRichInput = forwardRef(
  (
    {
      value,
      onChange,
      placeholder,
      handleDataMentions,
      renderSuggestion,
      allowSpaceInQuery = true,
      appendSpaceOnAdd = true,
      className,
      style,
      onPaste,
      onKeyDown,
      onClick,
      setStickerSuggestions,
      setShowSticker,
      stickerKeywords,
    },
    ref,
  ) => {
    const editorRef = useRef(null);

    const [showSuggestions, setShowSuggestions] = useState(false);
    const [filteredSuggestions, setFilteredSuggestions] = useState([]);
    const [selectionRange, setSelectionRange] = useState(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const { setKeydownMenuOn } = useMenuContext();

    const debounceSticker = useMemo(
      () => debounce((q) => fetchSticker(q), 600),
      [],
    );

    useEffect(() => {
      return () => debounceSticker.cancel();
    }, [debounceSticker]);

    const stickerReqId = useRef(0);

    const fetchSticker = async (keyword) => {
      const id = ++stickerReqId.current;
      const response = await apiFactory.stickerApi.getStickerByKeyword(keyword);
      if (id !== stickerReqId.current) return; // bỏ response cũ
      if (response?.status !== 200) return;
      setStickerSuggestions(response.data);
      setShowSticker(true);
    };

    // flag to avoid reacting to our own DOM changes
    const isInternalChange = useRef(false);

    // ---------- Helpers ----------

    // Get existing mention IDs currently present in editor DOM (preferred)
    const getExistingMentionIds = () => {
      if (editorRef.current) {
        const spans = editorRef.current.querySelectorAll(".mention-tag");
        if (spans && spans.length > 0) {
          return Array.from(spans).map((s) => s.dataset.userId);
        }
      }
      // fallback: parse from value prop
      if (value) {
        const ids = [];
        const re = /@\[(.+?)\]\((.+?)\)/g;
        let m;
        while ((m = re.exec(value)) !== null) {
          ids.push(m[2]);
        }
        return ids;
      }
      return [];
    };

    // Build markup string from editor DOM -> calls onChange
    const buildMarkupFromEditor = () => {
      if (!editorRef.current) return "";
      const nodes = Array.from(editorRef.current.childNodes);
      let out = "";

      nodes.forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          out += node.textContent;
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          if (node.classList.contains("mention-tag")) {
            const id = node.dataset.userId;
            const display = node.textContent.replace(/^@/, "");
            out += `@[${display}](${id})`;
          } else if (node.classList.contains("emoji-sizer")) {
            const unified = node.dataset.unified;
            if (unified) {
              const emojiChar = String.fromCodePoint(
                ...unified.split("-").map((u) => parseInt(u, 16)),
              );
              out += emojiChar;
            }
          } else {
            out += node.textContent;
          }
        }
      });
      return out;
    };

    // ---------- Selection save / restore ----------
    // Save selection as character offsets relative to container's textContent
    const saveSelection = (containerEl) => {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return null;
      const range = sel.getRangeAt(0);

      // if selection outside container, ignore
      if (!containerEl.contains(range.startContainer)) return null;

      const preSelectionRange = range.cloneRange();
      preSelectionRange.selectNodeContents(containerEl);
      preSelectionRange.setEnd(range.startContainer, range.startOffset);
      const start = preSelectionRange.toString().length;

      return {
        start,
        end: start + range.toString().length,
      };
    };

    const restoreSelection = (containerEl, savedSel) => {
      if (!savedSel) return;
      const charIndex = { count: 0 };
      const range = document.createRange();
      range.setStart(containerEl, 0);
      range.collapse(true);

      let nodeStack = [containerEl];
      let node;
      let foundStart = false;

      while ((node = nodeStack.shift())) {
        if (node.nodeType === 3) {
          // text
          const nextCount = charIndex.count + node.length;
          if (
            !foundStart &&
            savedSel.start >= charIndex.count &&
            savedSel.start <= nextCount
          ) {
            range.setStart(node, savedSel.start - charIndex.count);
            foundStart = true;
          }
          if (
            foundStart &&
            savedSel.end >= charIndex.count &&
            savedSel.end <= nextCount
          ) {
            range.setEnd(node, savedSel.end - charIndex.count);
            break;
          }
          charIndex.count = nextCount;
        } else {
          for (let i = 0; i < node.childNodes.length; i++) {
            nodeStack.push(node.childNodes[i]);
          }
        }
      }

      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    };

    // ---------- Suggestion fetch/filter ----------
    const mentionReqId = useRef(0);

    const fetchAndFilterSuggestions = (query) => {
      if (!handleDataMentions) {
        setFilteredSuggestions([]);
        return;
      }

      const currentId = ++mentionReqId.current;

      handleDataMentions(query, (list) => {
        if (currentId !== mentionReqId.current) return;

        const arr = Array.isArray(list) ? list : [];
        const existingIds = getExistingMentionIds();
        const filtered = arr.filter((i) => !existingIds.includes(i.id));
        setFilteredSuggestions(filtered);
        setActiveIndex(0);
      });
    };

    // ---------- Input / key handling ----------
    const getTextBeforeCursor = () => {
      const sel = window.getSelection();
      if (!sel || !sel.rangeCount) return "";

      const range = sel.getRangeAt(0).cloneRange();

      const preRange = document.createRange();
      preRange.setStart(editorRef.current, 0);
      preRange.setEnd(range.endContainer, range.endOffset);

      return preRange.toString();
    };

    const updateSuggestionsFromCursor = () => {
      const sel = window.getSelection();
      if (!sel || !sel.rangeCount) {
        setShowSuggestions(false);
        return;
      }

      const textBefore = getTextBeforeCursor();

      const regex = allowSpaceInQuery
        ? /(?:^|\s|[^\w@])@(?!@)([^@]*)$/
        : /(?:^|\s|[^\w@])@(?!@)([^\s@]*)$/;

      const match = textBefore.match(regex);

      if (match) {
        const atPos = textBefore.lastIndexOf("@");
        const afterAt = textBefore.slice(atPos);
        if (afterAt.includes(" ")) {
          setShowSuggestions(false);
          setFilteredSuggestions([]);
          return;
        }

        setSelectionRange(sel.getRangeAt(0).cloneRange());

        if (!showSuggestions) {
          setFilteredSuggestions([]);
        }

        setShowSuggestions(true);
        fetchAndFilterSuggestions(match[1]);
      } else {
        setShowSuggestions(false);
        setFilteredSuggestions([]);
      }
    };

    const rafId = useRef(null);

    const handleInput = () => {
      if (rafId.current) {
        cancelAnimationFrame(rafId.current);
      }

      rafId.current = requestAnimationFrame(() => {
        setKeydownMenuOn(false);
        const editor = editorRef.current;
        if (!editor) return;

        if (
          editor.innerHTML.trim() === "<br>" ||
          editor.innerHTML.trim() === ""
        ) {
          editor.innerHTML = "";
        }

        isInternalChange.current = true;
        updateSuggestionsFromCursor();

        const newMarkup = buildMarkupFromEditor();
        const mentions = Array.from(
          editor.querySelectorAll(".mention-tag"),
        ).map((s) => ({
          id: s.dataset.userId,
          display: s.textContent.replace(/^@/, ""),
        }));

        onChange?.({ target: { value: newMarkup }, mentions });

        setTimeout(() => {
          isInternalChange.current = false;
        }, 0);
      });
    };

    const handlePasteInternal = (e) => {
      e.preventDefault();
      const editor = editorRef.current;
      if (!editor) return;

      const sel = window.getSelection();
      const isReplaceAll =
        sel &&
        sel.rangeCount > 0 &&
        sel.toString().length === editor.textContent.length;

      let pasteText = (e.clipboardData || window.clipboardData).getData("text");

      if (isReplaceAll) {
        editor.innerHTML = "";
      }

      const escapeHTML = (str) => {
        return str
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;");
      };

      const parts = pasteText.split(
        /([\u{1F1E6}-\u{1F1FF}]{2})|((?:\p{Extended_Pictographic}|\p{Emoji_Presentation})\uFE0F?)/gu,
      );

      parts.forEach((part) => {
        if (!part) return;

        const emojiEntry = Object.entries(emojiMap).find(([key, val]) => {
          const emojiChar = String.fromCodePoint(
            ...val.unified.split("-").map((u) => parseInt(u, 16)),
          );
          return emojiChar === part;
        });

        if (emojiEntry) {
          const [emojiKey, emoji] = emojiEntry;
          const SPRITE_COLS = 62;

          const temp = document.createElement("div");
          createEmojiElement(temp, emojiKey, emoji, SPRITE_COLS);
          document.execCommand("insertHTML", false, temp.innerHTML);
        } else {
          const safeText = escapeHTML(part).replace(/ {2}/g, "&nbsp;&nbsp;");
          document.execCommand("insertHTML", false, safeText);
        }
      });

      requestAnimationFrame(() => {
        editor.scrollTop = editor.scrollHeight;
      });

      handleInput?.();
      onPaste?.(e);
    };

    const createEmojiElement = (editor, emojiKey, emoji, SPRITE_COLS) => {
      const emojiElement = document.createElement("span");
      emojiElement.className = "emoji-sizer emoji-outer";
      emojiElement.setAttribute("data-name", emojiKey);
      emojiElement.setAttribute("data-unified", emoji.unified);
      emojiElement.setAttribute("contenteditable", "false");

      const posX = (emoji.x / (SPRITE_COLS - 1)) * 100;
      const posY = (emoji.y / (SPRITE_COLS - 1)) * 100;

      emojiElement.style.backgroundImage = `url(${emojiSprite})`;
      emojiElement.style.backgroundPosition = `${posX}% ${posY}%`;
      emojiElement.style.backgroundSize = `${SPRITE_COLS * 100}% auto`;
      emojiElement.style.width = "23px";
      emojiElement.style.height = "23px";
      emojiElement.style.display = "inline-block";
      emojiElement.style.verticalAlign = "top";
      emojiElement.style.pointerEvents = "none";

      editor.appendChild(emojiElement);
      return emojiElement;
    };

    // ---------- Inserting mention from suggestion ----------
    const insertMention = (user) => {
      isInternalChange.current = true;

      let range = selectionRange ? selectionRange.cloneRange() : null;
      const sel = window.getSelection();
      if (!range) {
        if (!sel || !sel.rangeCount) return;
        range = sel.getRangeAt(0).cloneRange();
      }

      const textBefore = getTextBeforeCursor();
      const regex = allowSpaceInQuery
        ? /(?:^|\s|[^\w@])@(?!@)([^@]*)$/
        : /(?:^|\s|[^\w@])@(?!@)([^\s@]*)$/;

      const match = textBefore.match(regex);

      if (match) {
        let startNode = range.startContainer;
        let startOffset = range.startOffset;
        if (startNode.nodeType !== Node.TEXT_NODE) {
          let idx = startOffset - 1;
          while (idx >= 0 && startNode && startNode.childNodes) {
            const candidate = startNode.childNodes[idx];
            if (!candidate) {
              idx--;
              continue;
            }
            if (candidate.nodeType === Node.TEXT_NODE) {
              startNode = candidate;
              startOffset = candidate.textContent.length;
              break;
            } else if (candidate.nodeType === Node.ELEMENT_NODE) {
              startNode = null;
              break;
            }
            idx--;
          }
        }
        if (startNode && startNode.nodeType === Node.TEXT_NODE) {
          const fullMatch = match[0];
          const atPos = fullMatch.indexOf("@");
          const deleteLen = fullMatch.length - atPos;
          const delRange = document.createRange();
          const newStart = Math.max(0, startOffset - deleteLen);
          delRange.setStart(startNode, newStart);
          delRange.setEnd(range.startContainer, range.startOffset);
          delRange.deleteContents();
          range = delRange.cloneRange();
          range.collapse(true);
        }
      }

      const span = document.createElement("span");
      span.className = "mention-tag";
      span.contentEditable = "false";
      span.dataset.userId = user.id;
      span.textContent = user.id === "ALL" ? "@All" : `@${user.display}`;
      range.insertNode(span);

      const space = document.createTextNode(" ");
      span.parentNode.insertBefore(space, span.nextSibling);

      const newRange = document.createRange();
      newRange.setStartAfter(space);
      newRange.setEndAfter(space);
      sel.removeAllRanges();
      sel.addRange(newRange);

      // notify parent
      const newMarkup = buildMarkupFromEditor();
      const mentions = Array.from(
        editorRef.current.querySelectorAll(".mention-tag"),
      ).map((s) => ({
        id: s.dataset.userId,
        display: s.textContent.replace(/^@/, ""),
      }));
      if (onChange) onChange({ target: { value: newMarkup }, mentions });

      const existingIds = getExistingMentionIds();
      setFilteredSuggestions((prev) =>
        prev.filter((p) => !existingIds.includes(p.id)),
      );
      setShowSuggestions(false);

      // clear internal flag after a tick so useEffect won't stomp selection
      setTimeout(() => (isInternalChange.current = false), 0);
    };

    // ---------- Select-all highlight logic ----------

    // ---------- Insert emoji ----------
    const insertEmoji = (emojiKey) => {
      const editor = editorRef.current;
      if (!editor) return;

      const emoji = emojiMap[emojiKey];
      if (!emoji) return;
      const SPRITE_COLS = 62;

      isInternalChange.current = true;

      const emojiElement = createEmojiElement(
        editor,
        emojiKey,
        emoji,
        SPRITE_COLS,
      );

      const range = document.createRange();
      range.setStartAfter(emojiElement);
      range.collapse(true);

      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);

      // notify parent
      handleInput?.();

      // focus and clear internal flag next tick
      editor.focus();
      setTimeout(() => (isInternalChange.current = false), 0);
    };

    useImperativeHandle(ref, () => ({
      insertEmoji,
      focus: () => editorRef.current?.focus(),
      clear: () => {
        if (editorRef.current) editorRef.current.innerHTML = "";
      },
      getContent: () => buildMarkupFromEditor(),
    }));

    // ---------- Key handling (arrows, enter, escape, @) ----------
    const handleKeyDownInternal = (e) => {
      if (e.key === "@") {
        setTimeout(() => updateSuggestionsFromCursor(), 0);
        return;
      }

      if (showSuggestions && filteredSuggestions.length > 0) {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setActiveIndex((prev) => {
            const next = (prev + 1) % filteredSuggestions.length;
            scrollToActiveItem(next);
            return next;
          });
          return;
        }

        if (e.key === "ArrowUp") {
          e.preventDefault();
          setActiveIndex((prev) => {
            const next =
              (prev - 1 + filteredSuggestions.length) %
              filteredSuggestions.length;
            scrollToActiveItem(next);
            return next;
          });
          return;
        }

        if (e.key === "Enter") {
          e.preventDefault();
          const selected = filteredSuggestions[activeIndex];
          if (selected) insertMention(selected);
          return;
        }

        if (e.key === "Escape") {
          e.preventDefault();
          setShowSuggestions(false);
          return;
        }
      }

      if (e.key === "Backspace") {
        const sel = window.getSelection();
        if (sel && sel.rangeCount > 0) {
          const range = sel.getRangeAt(0);
          const { startContainer, startOffset } = range;

          let node = startContainer;
          if (node.nodeType === Node.TEXT_NODE) {
            if (startOffset === 0 && node.previousSibling) {
              const prev = node.previousSibling;
              if (
                prev.tagName === "IMG" &&
                prev.classList.contains("emoji-node")
              ) {
                e.preventDefault();
                prev.remove();
                handleInput();
                return;
              }
            }
          } else if (startOffset > 0) {
            const prev = node.childNodes[startOffset - 1];
            if (
              prev &&
              prev.tagName === "IMG" &&
              prev.classList.contains("emoji-node")
            ) {
              e.preventDefault();
              prev.remove();
              handleInput();
              return;
            }
          }
        }
      }

      if (e.key === "Enter" && !showSuggestions) {
        onKeyDown?.(e);
      }
    };

    const scrollToActiveItem = (index) => {
      const container = document.querySelector(".mention-suggestions");
      if (!container) return;
      const items = container.querySelectorAll("li");
      const activeItem = items[index];
      if (activeItem) {
        const itemTop = activeItem.offsetTop;
        const itemBottom = itemTop + activeItem.offsetHeight;
        const containerScrollTop = container.scrollTop;
        const containerHeight = container.offsetHeight;

        if (itemTop < containerScrollTop) {
          container.scrollTop = itemTop;
        } else if (itemBottom > containerScrollTop + containerHeight) {
          container.scrollTop = itemBottom - containerHeight;
        }
      }
    };

    // click/focus inside editor -> update suggestions (small delay for selection)
    const handleClickOrFocus = (ev) => {
      setTimeout(() => updateSuggestionsFromCursor(), 0);
      if (onClick) onClick(ev);
    };

    // ---------- Keep editor.innerHTML in sync with value (preserve caret) ----------
    useEffect(() => {
      const editor = editorRef.current;
      if (!editor) return;
      // if (isInternalChange.current) return;

      const saved = saveSelection(editor);

      if (!value || value.trim() === "") {
        editor.innerHTML = "";
        requestAnimationFrame(() => restoreSelection(editor, saved));

        const sel = window.getSelection();
        if (sel) sel.removeAllRanges();
        editor.focus();
      }
    }, [value]);

    useEffect(() => {
      const editorContent = editorRef.current ? buildMarkupFromEditor() : value;
      const trimmedContent = editorContent?.trim();

      if (
        trimmedContent &&
        stickerKeywords?.includes(trimmedContent?.toLowerCase())
      ) {
        debounceSticker(trimmedContent);
      } else {
        setShowSticker(false);
      }
    }, [value]);

    useEffect(() => {
      const handleSelectionChange = () => {
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return;

        const range = sel.getRangeAt(0);
        const editor = editorRef.current;
        if (!editor) return;

        editor.querySelectorAll(".emoji-sizer.selected").forEach((el) => {
          el.classList.remove("selected");
        });

        const emojis = editor.querySelectorAll(".emoji-sizer");
        emojis.forEach((el) => {
          if (range.intersectsNode(el)) {
            el.classList.add("selected");
          }
        });
      };

      document.addEventListener("selectionchange", handleSelectionChange);
      return () =>
        document.removeEventListener("selectionchange", handleSelectionChange);
    }, []);

    useEffect(() => {
      const handleClickOutside = (e) => {
        const editor = editorRef.current;
        if (
          editor &&
          !editor.contains(e.target) &&
          !e.target.closest(".mention-suggestions")
        ) {
          setShowSuggestions(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
      const editor = editorRef.current;
      if (!editor) return;

      const handleGlobalKeyDown = (e) => {
        const isCtrlA = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "a";

        const activeEl = document.activeElement;
        const tag = activeEl?.tagName?.toLowerCase();

        const isTypingInInput =
          tag === "input" || tag === "textarea" || activeEl?.isContentEditable;

        const modalVisible = Array.from(
          document.querySelectorAll(".ant-modal"),
        ).some(
          (modal) =>
            modal.offsetParent !== null && modal.style.display !== "none",
        );

        if ((isTypingInInput && activeEl !== editor) || modalVisible) return;

        if (isCtrlA) {
          e.preventDefault();
          if (document.activeElement === editor) {
            const range = document.createRange();
            range.selectNodeContents(editor);
            const sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(range);
          }
          return;
        }

        if (document.activeElement === editor) return;

        if (!e.ctrlKey && !e.metaKey && !e.altKey) {
          if (e.key.length === 1) {
            e.preventDefault();
            editor.focus();

            const sel = window.getSelection();
            const range = document.createRange();
            range.selectNodeContents(editor);
            range.collapse(false);
            sel.removeAllRanges();
            sel.addRange(range);

            document.execCommand("insertText", false, e.key);
            handleInput();
          } else if (e.key === "Backspace") {
            e.preventDefault();
            const sel = window.getSelection();
            const range = sel.getRangeAt(0);
            if (range) {
              if (!range.collapsed) {
                range.deleteContents();
              } else if (editor.textContent.length > 0) {
                editor.textContent = editor.textContent.slice(0, -1);
              }
              handleInput();
            }
          } else if (e.key === "Enter") {
            e.preventDefault();
            onKeyDown && onKeyDown(e);
          }
        }
      };

      window.addEventListener("keydown", handleGlobalKeyDown);
      return () => window.removeEventListener("keydown", handleGlobalKeyDown);
    }, [handleInput]);

    // ---------- Render ----------
    return (
      <div
        className={`mention-container ${className || ""}`}
        style={{ position: "relative", ...style }}
      >
        <div
          ref={editorRef}
          className="mention-editor"
          contentEditable
          suppressContentEditableWarning
          onInput={handleInput}
          onKeyDown={handleKeyDownInternal}
          onPaste={handlePasteInternal}
          onClick={handleClickOrFocus}
          data-placeholder={placeholder}
          onBlur={() => setKeydownMenuOn(true)}
        />
        {showSuggestions &&
          filteredSuggestions &&
          filteredSuggestions.length > 0 && (
            <ul className="mention-suggestions">
              {filteredSuggestions.map((item, idx) => (
                <li
                  key={item.id}
                  className={idx === activeIndex ? "active" : ""}
                  onMouseDown={(e) => {
                    e.preventDefault(); // prevent blur
                    insertMention(item);
                  }}
                >
                  {renderSuggestion ? renderSuggestion(item) : item.display}
                </li>
              ))}
            </ul>
          )}
      </div>
    );
  },
);
